import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/*
 * MarketBoard — ambient "living trading screen" primitives for the Partners
 * section: a seamlessly-scrolling background sparkline, a slow market tape, a
 * terminal grid, and ticking numbers. All movement is CSS-driven (cheap) and
 * pauses when the section leaves the viewport; numbers tick from one interval.
 * Everything is illustrative — NOT real market data. No green/red, no neon.
 */

export const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/* Build a wave path that tiles seamlessly over `period` (two tiles drawn, so a
   translateX(-50%) of a 200%-wide SVG loops with no seam). */
export function genWavePath(seed = 0, period = 300, height = 70, stepsPerTile = 40) {
  const p1 = 2 + (seed % 2);
  const p2 = 3 + ((seed + 1) % 3);
  const a1 = 9 + (seed % 3) * 2.5;
  const a2 = 4 + ((seed + 2) % 3) * 2;
  const ph1 = seed * 0.9;
  const ph2 = seed * 1.7 + 1;
  const mid = height / 2;
  const total = period * 2;
  const steps = stepsPerTile * 2;
  let d = "";
  for (let i = 0; i <= steps; i++) {
    const x = (i / steps) * total;
    const t = (x / period) * Math.PI * 2;
    const y = mid + a1 * Math.sin(p1 * t + ph1) + a2 * Math.sin(p2 * t + ph2);
    d += (i === 0 ? "M" : "L") + " " + x.toFixed(1) + " " + y.toFixed(1) + " ";
  }
  return d.trim();
}

/* A full-width background line that scrolls slowly and perpetually. */
export function BoardLine({ seed = 0, color = "#BEE5E0", opacity = 0.12, duration = 70, period = 300, height = 70 }) {
  const d = useMemo(() => genWavePath(seed, period, height), [seed, period, height]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <svg
        className="nxx-board-line absolute left-0 top-0 h-full"
        style={{ width: "200%", "--dur": `${duration}s`, opacity }}
        viewBox={`0 0 ${period * 2} ${height}`}
        preserveAspectRatio="none"
        fill="none"
      >
        <path
          d={d}
          stroke={color}
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

/* A thin, slow market tape of abstract indices. */
export function MarketTape({ items, duration = 90, className }) {
  const Row = (
    <div className="flex shrink-0 items-center">
      {items.map((it, i) => (
        <span
          key={i}
          className="mx-5 inline-flex items-center gap-2"
          style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.08em" }}
        >
          <span style={{ color: "var(--nexxis-ink-muted)", opacity: 0.75 }}>{it.label}</span>
          <span style={{ color: "var(--nexxis-ink)", opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>
            {it.value}
          </span>
          <span
            className="inline-block h-1 w-1 rounded-full"
            style={{ background: "rgba(255,104,87,0.5)" }}
          />
        </span>
      ))}
    </div>
  );
  return (
    <div className={cn("relative overflow-hidden", className)} aria-hidden="true">
      <div className="nxx-board-tape flex w-max" style={{ "--dur": `${duration}s` }}>
        {Row}
        {Row}
      </div>
    </div>
  );
}

/* Section-wide terminal grid (theme-aware colours live in index.css). */
export function TerminalGrid({ opacity = 1 }) {
  return (
    <div
      aria-hidden="true"
      className="nxx-board-grid pointer-events-none absolute inset-0"
      style={{
        opacity,
        WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 50% 45%, #000 60%, transparent 100%)",
        maskImage: "radial-gradient(ellipse 80% 70% at 50% 45%, #000 60%, transparent 100%)",
      }}
    />
  );
}

/* One interval drives small ticks for all values; stops when inactive. */
export function useBoardTicks(seeds, { active, reduced, intervalMs = 2800 }) {
  const [values, setValues] = useState(() => seeds.slice());

  useEffect(() => {
    if (reduced || !active) return;
    const id = window.setInterval(() => {
      setValues((prev) =>
        prev.map((v, i) => {
          // small, slow drift; mean-reverting toward the seed so it stays near base
          const pull = (seeds[i] - v) * 0.06;
          const jitter = (Math.random() - 0.5) * Math.max(2, seeds[i] * 0.006);
          return Math.max(1, Math.round(v + pull + jitter));
        })
      );
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [active, reduced, intervalMs, seeds]);

  return values;
}

/* A tabular number that flashes briefly (white) when its value changes. */
export function TickingNumber({ value, reduced, className, style }) {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);

  useEffect(() => {
    if (reduced) return;
    if (prev.current !== value) {
      prev.current = value;
      setFlash(true);
      const t = window.setTimeout(() => setFlash(false), 460);
      return () => window.clearTimeout(t);
    }
  }, [value, reduced]);

  return (
    <span
      className={className}
      style={{
        fontFamily: MONO,
        fontVariantNumeric: "tabular-nums",
        color: flash ? "#FFFFFF" : "rgba(190,229,224,0.9)",
        transition: flash ? "none" : "color 460ms ease",
        ...style,
      }}
    >
      {Math.round(value).toLocaleString("en-US")}
    </span>
  );
}
